/**
 * 슬라임 경주 — 규칙과 셈 (클라이언트 미러).
 *
 * ⚠ 서버 `src/game/race.ts` 와 **같은 값을 내야 한다.**
 *   화면이 배당을 미리 보여주는데 그 셈이 서버와 다르면 "표시된 것과 다르게
 *   받았다" 가 된다. 돈이 걸린 화면에서 그건 버그가 아니라 사고다.
 *   `check-client` 가 상수를 대조하고, `check-race` 가 **양쪽 셈을 실제로 돌려**
 *   같은 값을 내는지 본다.
 */

export const SLIMES = 6;
export const RACE_BET = 120;
export const RACE_RUN = 40;
export const RACE_SHOW = 20;
export const RACE_PERIOD = RACE_BET + RACE_RUN + RACE_SHOW;
/**
 * **패자 판돈 수수료 5%** (HN). 승자 판돈에는 손대지 않아 맞히고도 원금보다
 * 적게 받던 옛 결함을 되살리지 않는다. 수수료도 소각하지 않고 다음 누적 상금으로 간다.
 * 서버 `src/game/race.ts` 와 반드시 같아야 한다 — 돈 계산이 갈라지면
 * "표시된 것과 다르게 받았다" 가 된다(check-race 가 대조한다).
 */
export const HOUSE_EDGE = 0.05;
export const WIN_BONUS_RATE = 0.05;
export const BET_MIN = 10;
export const CARRY_RELEASE_RATE = 0.01;
export const CARRY_RELEASE_POOL_RATE = 0.04;
export const CARRY_USE_MULTIPLIER_MIN = 0;
export const CARRY_USE_MULTIPLIER_DEFAULT = 1;
export const RACE_CARRY_SEED = 100_000_000;
export const SLIME_NAMES = ['말랑이', '뿌직이', '탱글이', '쭈굴이', '반짝이', '툭툭이'];

/**
 * 컨디션 다섯 눈금. **서버 `src/game/race.ts` 의 `CONDS` 와 같아야 한다.**
 *
 * 사용자: *"최상이라고 늘 이기는 게 아니고 최하라고 늘 지는 게 아니다"* ·
 * *"티 좀 나게 해줘"*. 그래서 **원인이 아니라 증거**다 — 서버는 1등을 뽑은 **뒤**에
 * 컨디션을 만든다(`condOf`). 실측 12만 판에서 최상 26.5% · 최하 9.0%(기준선 16.7%),
 * 즉 **최고 컨디션이 지는 판이 73.5%** 다. 읽을 거리는 되지만 답은 아니다.
 *
 * 색은 다섯이 **한눈에 갈리도록** 잡았다 — 이름만 적으면 여섯 줄에서 안 읽힌다.
 */
export const CONDS = ['최하', '나쁨', '보통', '좋음', '최상'];
export const COND_COL = ['#8a7f6b', '#a98a6b', '#c9c0aa', '#8cbd87', '#ffd08a'];

/** 슬라임마다 다른 색 — 여섯이 같은 색이면 어느 게 내 것인지 못 본다 */
export const SLIME_COL = ['#7aa35c', '#c9736c', '#7fa8c9', '#c2a05c', '#a98cc9', '#6fb5a8'];

export function normalizeCarryUseMultiplier(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return CARRY_USE_MULTIPLIER_DEFAULT;
  return Math.max(CARRY_USE_MULTIPLIER_MIN, Number(n.toFixed(2)));
}

export function carryRelease(pool, winner, carry = 0, useMultiplier = CARRY_USE_MULTIPLIER_DEFAULT) {
  const total = pool.reduce((a, b) => a + b, 0);
  const win = pool[winner] ?? 0;
  const saved = Math.max(0, Math.floor(carry));
  if (total <= 0 || win <= 0 || saved <= 0) return 0;
  const mult = normalizeCarryUseMultiplier(useMultiplier);
  if (mult <= 0) return 0;
  const scaledRateCap = Math.max(1, Math.floor(saved * CARRY_RELEASE_RATE * mult));
  const scaledPoolCap = Math.max(1, Math.floor(total * CARRY_RELEASE_POOL_RATE * mult));
  return Math.min(saved, scaledRateCap, scaledPoolCap);
}

export function prizePool(pool, winner, carry = 0, useMultiplier = CARRY_USE_MULTIPLIER_DEFAULT) {
  const total = pool.reduce((a, b) => a + b, 0);
  const win = pool[winner] ?? 0;
  if (total <= 0 || win <= 0) return 0;
  const losing = Math.max(0, total - win);
  const commission = Math.floor(losing * HOUSE_EDGE);
  return total - commission + carryRelease(pool, winner, carry, useMultiplier);
}

export function payout(pool, winner, bet, carry = 0, useMultiplier = CARRY_USE_MULTIPLIER_DEFAULT) {
  if (bet <= 0) return 0;
  const win = pool[winner] ?? 0;
  if (win <= 0) return 0;
  const share = prizePool(pool, winner, carry, useMultiplier);
  const got = Number((BigInt(share) * BigInt(bet)) / BigInt(win));
  const bonus = Math.max(1, Math.floor(bet * WIN_BONUS_RATE));
  return Math.max(got, bet + bonus);
}

export function odds(pool, slime, carry = 0, useMultiplier = CARRY_USE_MULTIPLIER_DEFAULT) {
  const win = pool[slime] ?? 0;
  if (win <= 0) return 0;
  return prizePool(pool, slime, carry, useMultiplier) / win;
}

export function winnerOf(seedHashHex) {
  const n = parseInt(seedHashHex.slice(0, 8), 16);
  return Number.isFinite(n) ? n % SLIMES : 0;
}

export function positions(seedHashHex, winner, t) {
  const clamp = Math.max(0, Math.min(1, t));
  const out = [];
  for (let i = 0; i < SLIMES; i++) {
    const a = parseInt(seedHashHex.slice(8 + i * 2, 10 + i * 2), 16) || 1;
    const wobble = Math.sin(clamp * Math.PI * (2 + (a % 5)) + (a % 17)) * 0.06;
    const bias = i === winner ? 0.06 * clamp : -0.02 * clamp * ((a % 7) / 7);
    out.push(Math.max(0, Math.min(1, clamp + wobble * (1 - clamp) + bias)));
  }
  if (clamp >= 1) for (let i = 0; i < SLIMES; i++) out[i] = i === winner ? 1 : Math.min(0.985, out[i]);
  return out;
}
